import * as React from 'react';

interface UseAssetSelectionOptions {
  /**
   * Ids of the assets shown now, in grid order. Shift ranges follow this order, "Select visible" takes all of them,
   * and ids that leave it drop out of the selection.
   */
  orderedIds: string[];
  /**
   * Selection starts over whenever this changes: pass everything that changes what is shown, like the collection,
   * filters and sort (SDD D59), because "Select visible" depends on it.
   */
  resetKey: string;
}

interface SelectionState {
  resetKey: string;
  ids: ReadonlySet<string>;
  /** Last asset clicked without Shift, where a Shift range starts. */
  anchorId: string | null;
}

const emptyState = (resetKey: string): SelectionState => ({ resetKey, ids: new Set(), anchorId: null });

/**
 * Multi-select state for an asset grid: click toggles one asset, Shift-click sets the whole range from the last
 * clicked asset to the same state, like in Classic and file managers (SDD D41).
 *
 * @example
 * const selection = useAssetSelection({ orderedIds: assets.map((a) => a.id), resetKey: `${collectionId}|${sort}` });
 * <Asset.Checkbox isSelected={selection.isSelected(id)} onSelectedChange={(_, { shiftKey }) => selection.toggle(id, { shiftKey })} />
 */
function useAssetSelection({ orderedIds, resetKey }: UseAssetSelectionOptions) {
  const [state, setState] = React.useState<SelectionState>(() => emptyState(resetKey));
  // A stale key means what is shown changed: read as empty right away, without a render that shows old selection
  const current = state.resetKey === resetKey ? state : emptyState(resetKey);

  const visibleIds = React.useMemo(() => new Set(orderedIds), [orderedIds]);
  const selectedIds = React.useMemo(
    () => new Set([...current.ids].filter((id) => visibleIds.has(id))),
    [current.ids, visibleIds],
  );

  const toggle = React.useCallback(
    (id: string, { shiftKey = false }: { shiftKey?: boolean } = {}) => {
      setState((previous) => {
        const base = previous.resetKey === resetKey ? previous : emptyState(resetKey);
        const ids = new Set(base.ids);
        const nextIsSelected = !ids.has(id);
        const anchorIndex = base.anchorId ? orderedIds.indexOf(base.anchorId) : -1;
        const targetIndex = orderedIds.indexOf(id);

        if (shiftKey && anchorIndex !== -1 && targetIndex !== -1) {
          const [from, to] = anchorIndex < targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex];
          for (const rangeId of orderedIds.slice(from, to + 1)) {
            if (nextIsSelected) ids.add(rangeId);
            else ids.delete(rangeId);
          }
          // The anchor stays, so the next Shift-click still measures from the first click
          return { resetKey, ids, anchorId: base.anchorId };
        }

        if (nextIsSelected) ids.add(id);
        else ids.delete(id);
        return { resetKey, ids, anchorId: id };
      });
    },
    [orderedIds, resetKey],
  );

  const selectVisible = React.useCallback(
    (onScreenIds: string[] = orderedIds) => {
      const shown = new Set(orderedIds);
      setState((previous) => ({
        resetKey,
        ids: new Set(onScreenIds.filter((id) => shown.has(id))),
        anchorId: previous.resetKey === resetKey ? previous.anchorId : null,
      }));
    },
    [orderedIds, resetKey],
  );

  const clear = React.useCallback(() => setState(emptyState(resetKey)), [resetKey]);

  const isSelected = React.useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const isEverySelected = React.useCallback(
    (ids: string[]) => ids.every((id) => !visibleIds.has(id) || selectedIds.has(id)),
    [visibleIds, selectedIds],
  );

  return { selectedIds, isSelected, isEverySelected, toggle, selectVisible, clear };
}

const ASSET_ID_ATTRIBUTE = 'data-asset-id';

function getScrollViewportBounds(container: HTMLElement): { top: number; bottom: number } {
  const viewport = container.closest('main')?.getBoundingClientRect();
  return {
    top: Math.max(viewport?.top ?? 0, 0),
    bottom: Math.min(viewport?.height ? viewport.bottom : window.innerHeight, window.innerHeight),
  };
}

function getOnScreenAssetIds(container: HTMLElement | null): string[] {
  if (!container) return [];
  const { top, bottom } = getScrollViewportBounds(container);
  return [...container.querySelectorAll<HTMLElement>(`[${ASSET_ID_ATTRIBUTE}]`)]
    .filter((tile) => {
      const rect = tile.getBoundingClientRect();
      return rect.height > 0 && rect.bottom > top && rect.top < bottom;
    })
    .map((tile) => tile.getAttribute(ASSET_ID_ATTRIBUTE) ?? '')
    .filter(Boolean);
}

const isSameIds = (a: string[], b: string[]) => a.length === b.length && a.every((id, index) => id === b[index]);

function useOnScreenAssetIds(
  containerRef: React.RefObject<HTMLElement | null>,
  { isEnabled, contentKey }: { isEnabled: boolean; contentKey: unknown },
): string[] {
  const [ids, setIds] = React.useState<string[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the tiles changed, not something the effect reads
  React.useEffect(() => {
    if (!isEnabled) return;
    const measure = () => {
      const next = getOnScreenAssetIds(containerRef.current);
      setIds((previous) => (isSameIds(previous, next) ? previous : next));
    };
    let frame = 0;
    const measureNextFrame = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    document.addEventListener('scroll', measureNextFrame, { capture: true, passive: true });
    window.addEventListener('resize', measureNextFrame);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('scroll', measureNextFrame, { capture: true });
      window.removeEventListener('resize', measureNextFrame);
    };
  }, [containerRef, isEnabled, contentKey]);

  return isEnabled ? ids : [];
}

export { ASSET_ID_ATTRIBUTE, getOnScreenAssetIds, useAssetSelection, useOnScreenAssetIds };
export type { UseAssetSelectionOptions };
